import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { BillingMethod } from '../../models/business/PolicyShift'
import { billingMethodSchema } from '../../models/schema'
import {
  createBillingMethod as createBillingMethodAPI,
  deleteBillingMethod as deleteBillingMethodAPI,
  fetchBillingMethodById,
  fetchBillingMethods,
  updateBillingMethod as updateBillingMethodAPI,
} from '../../services/business/BillingMethodService'
import { AppDispatch, RootState } from './../store'

const initialState: SBState<BillingMethod> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'billingMethod',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getBillingMethodsSuccess,
  setQuery: setBillingMethodQuery,
  reset: resetBillingMethodState,
  resetQueryAndIds: resetBillingMethodQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: BillingMethod }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectBillingMethods = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedBillingMethod = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectBillingMethodById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectBillingMethodsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedBillingMethod =
  (billingMethod: BillingMethod | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<BillingMethod>(billingMethod))
    )

export const getBillingMethods = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchBillingMethods(params),
    [billingMethodSchema],
    slice.actions
  )

export const createBillingMethod = (billingMethod: BillingMethod) =>
  SBAPI.CreateDispatched(
    createBillingMethodAPI(billingMethod),
    billingMethodSchema,
    slice.actions
  )

export const getBillingMethodWithId = (id: number) =>
  SBAPI.FetchDispatched<BillingMethod>(
    fetchBillingMethodById(id),
    billingMethodSchema,
    slice.actions
  )

export const updateBillingMethod = (billingMethod: BillingMethod) =>
  SBAPI.UpdateDispatched<BillingMethod>(
    updateBillingMethodAPI(billingMethod),
    billingMethod,
    slice.actions
  )

export const deleteBillingMethod = (billingMethod: BillingMethod) =>
  SBAPI.DestroyDispatched<BillingMethod>(
    deleteBillingMethodAPI(billingMethod),
    billingMethod,
    slice.actions
  )
